import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env';
import { CartService } from '@features/cart/services/cart.service';
import {
  ProductDetailShop,
  ProductDetailVariant,
  ProductDetailVariantTier1,
  ProductDetailVariantTier2,
} from '@features/product/interfaces/product-detail.interface';
import { Product } from '@features/product/interfaces/product.interface';
import { ProductDetailService } from '@features/product/services/product-detail.service';
import { ProductService } from '@features/product/services/product.service';
import { WishListService } from '@features/user/services/wisthlist.service';
import {
  faCommentAlt,
  faHeart,
  faInfoCircle,
  faPlus,
  faShare,
  faShoppingCart,
  faSpinner,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FcSharingComponent } from '@shared/components/fc-sharing/fc-sharing.component';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { SwiperOptions } from 'swiper';

@Component({
  selector: 'fc-mobile-product-detail',
  templateUrl: './mobile-product-detail.component.html',
  styleUrls: ['./mobile-product-detail.component.css'],
  animations: [
    trigger('showTopBar', [
      state(
        'hide',
        style({
          top: '-64px',
        })
      ),
      state(
        'show',
        style({
          top: '64px',
        })
      ),
      transition('hide <=> show', [animate('.75s ease')]),
    ]),
    trigger('descShowMore', [
      state(
        'hide',
        style({
          height: '{{descHiddenHeight}}',
        }),
        { params: { descHiddenHeight: '3em' } }
      ),
      state(
        'show',
        style({
          height: '{{descExpandedHeight}}',
        }),
        { params: { descExpandedHeight: '3em' } }
      ),
      transition('hide <=> show', [animate('.75s ease')]),
    ]),
    trigger('brochureShowMore', [
      state(
        'hide',
        style({
          height: '200px',
        })
      ),
      state(
        'show',
        style({
          height: '{{brochureExpandedHeight}}',
        }),
        { params: { brochureExpandedHeight: '3em' } }
      ),
      transition('hide <=> show', [animate('.75s ease')]),
    ]),
  ],
})
export class MobileProductDetailComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();

  webUrl = environment.webUrl;
  // fontawesome
  faStar = faStar;
  faCommentAlt = faCommentAlt;
  faInfoCircle = faInfoCircle;
  faShoppingCart = faShoppingCart;
  faSpinner = faSpinner;
  faHeart = faHeart;
  faPlus = faPlus;
  faShare = faShare;

  // swiper
  config: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 5,
    speed: 300,
    loop: true,
    loopedSlides: 2,
    autoplay: {
      delay: 5000,
    },
    pagination: {
      type: 'fraction',
    },
  };
  recommendedConfig: SwiperOptions = {
    slidesPerView: 2,
    spaceBetween: 12,
    loop: false,
    loopedSlides: 1,
    freeMode: true,
    navigation: true,
    breakpoints: {
      640: {
        slidesPerView: 4,
      },
      768: {
        slidesPerView: 6,
      },
    },
  };

  slug: string = String(this.route.snapshot.paramMap.get('slug'));
  imageActiveIndex: number = 0;

  //
  @ViewChild('detailContainer') detailContainer?: ElementRef;
  @ViewChild('detailBoxContainer') detailBoxContainer?: ElementRef;
  // topbar
  isShowTopBar: boolean = false;
  activeNavigation = 'description';
  // variant variables
  variantTier1: ProductDetailVariantTier1 = {} as ProductDetailVariantTier1;
  variantTier2: ProductDetailVariantTier2 = {} as ProductDetailVariantTier2;
  availableVariantsTier1: any = [];
  availableVariantsTier2: any = [];
  selectedVariantTier1Name: string | null = null;
  selectedVariantTier2Name: string | null = null;
  variants: ProductDetailVariant[] = [];
  selectedVariant: ProductDetailVariant | null = null;
  //
  addToCartError = null;
  qtyInputChangeInterval: any;
  //
  shop?: ProductDetailShop;
  shopVouchers: any[] = []; // note: need shop
  shopDefaultLogo?: string;
  //
  @ViewChild('descContainer') descContainer?: ElementRef;
  @ViewChild('descSeeMoreBtnContainer') descSeeMoreBtnContainer?: ElementRef;
  isDescExpanded: boolean = false;
  descHiddenHeight?: number;
  descExpandedHeight?: number;
  //
  @ViewChild('brochureContainer') brochureContainer?: ElementRef;
  isBrochureExpanded: boolean = false;
  showStickyBrochureCloseBtn: boolean = false;
  brochureExpandedHeight?: number;
  //
  @ViewChild('addToCartModal') addToCartModal?: ElementRef;
  @ViewChild('reviewContainer') reviewContainer?: ElementRef;
  @ViewChild('discussionContainer') discussionContainer?: ElementRef;
  @ViewChild('recommendationContainer') recommendationContainer?: ElementRef;

  routerSubscription?: Subscription;

  addToCartLoading = false;
  buyNowLoading = false;

  locationUrl = '';

  cartForm: FormGroup = new FormGroup({
    quantity: new FormControl(1),
  });
  selectedImage: string = '';
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private layoutService: LayoutService,
    private productDetailService: ProductDetailService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private cartService: CartService,
    private productService: ProductService,
    private wishlistService: WishListService,
    private metaService: Meta,
    private title: Title,
    private dialogService: DialogService
  ) {
    if (isPlatformBrowser(platformId)) {
      if (!this.layoutService.isMobile$.value) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
          this.router.navigate(['/product', this.slug], {
            replaceUrl: true,
          })
        );
      } else {
        this.layoutService.setFooterVisibility(false);
        this.layoutService.setMobileLayoutConfig({
          headerNav: {
            backButton: true,
            searchButton: false,
            title: 'Product Detail',
            notificationButton: false,
            userButton: true,
          },
          bottomNav: {
            visibillity: false,
          },
        });
      }
    }
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false; // if diff slug then refresh
    this.getProductDetail();
  }
  ngOnDestroy(): void {
    this.layoutService.setFooterVisibility(true);
    this.layoutService.setDefaultMobileLayoutConfig();
    this.destroy$.next();
    this.destroy$.complete();
  }
  updateMetaTags() {
    // Main Meta Tags
    this.title.setTitle(this.product.name);
    this.metaService.updateTag({
      name: 'title',
      content: String(this.product.name),
    });
    this.metaService.updateTag({
      name: 'description',
      content: String(this.product.description),
    });
    // OG Meta Tags
    this.metaService.updateTag({
      property: 'og:title',
      content: String(this.product.name),
    });
    this.metaService.updateTag({
      property: 'og:description',
      content: String(this.product.description),
    });
    this.metaService.updateTag({
      property: 'og:url',
      content: String(this.webUrl + 'product/detail/' + this.slug),
    });
    this.metaService.updateTag({
      property: 'og:image',
      content: String(this.productDetail.images[0].medium.path),
    });
    // Twitter
    this.metaService.updateTag({
      property: 'twitter:title',
      content: String('Egg Story - ' + this.product.name),
    });
    this.metaService.updateTag({
      property: 'twitter:url',
      content: String(this.webUrl + 'product/detail/' + this.slug),
    });
    this.metaService.updateTag({
      property: 'twitter:description',
      content: String(this.product.description),
    });
    this.metaService.updateTag({
      property: 'twitter:image',
      content: String(this.productDetail.images[0].medium.path),
    });
  }
  productDetail: any;
  imagesDisplayed: any;
  product: Product = {} as Product;
  productDescription: any;
  breadcrumbs: any;
  safeVideoUrl: any;
  brochures: any = [];

  getProductDetail() {
    this.productDetailService
      .getProductDetail(this.slug)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.productDetail = data;
        this.imagesDisplayed = data.images;
        this.product = data.product;
        this.productDescription = data.product.description
          ? this.sanitizer.bypassSecurityTrustHtml(data.product.description)
          : null;
        this.breadcrumbs = data.breadcrumbsDisplayed;
        this.safeVideoUrl = data.product.video_link
          ? this.sanitizer.bypassSecurityTrustResourceUrl(
              data.product.video_link
            )
          : null;
        this.variantTier1 = data.variant_tier_1;
        this.variantTier2 = data.variant_tier_2;
        this.variants = data.variants;
        this.shop = data.shop;
        this.brochures = data.brochures;
        this.setupAvailableVariant();
        // note: need shop
        // this.shopService.getShopVouchers(this.shop.id).subscribe((vouchers) => {
        //   this.shopVouchers = vouchers.slice(0,3);
        // });
        this.updateMetaTags();
        if (this.variantTier1.name) {
          this.onSelectVariantTier1(this.variantTier1.variants[0]);
        }
        if (this.variantTier2.name) {
          this.onSelectVariantTier1(this.variantTier2.variants[0]);
        }
        if (isPlatformBrowser(this.platformId)) {
          let descHeightInterval = setInterval(() => {
            this.setupDescHeight();
            clearInterval(descHeightInterval);
          }, 500);
        }
        this.getSimiliarRecommendedProducts();
      });
  }

  recomendedProducts: any;
  getSimiliarRecommendedProducts() {
    this.productService
      .getRecommendSimilarProducts(this.slug, 12)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.recomendedProducts = res;
      });
  }
  setupAvailableVariant() {
    this.variants.forEach((variant: any) => {
      this.availableVariantsTier1.push(variant.variant_name_1);
      this.availableVariantsTier2.push(variant.variant_name_2);
    });
  }

  // manage show description more / less
  setupDescHeight() {
    // if(this.mediaSize == 'lg') {
    //   let descContainerHeight = this.descContainer.nativeElement.scrollHeight;
    //   let height = `${descContainerHeight}px`;
    //   if(descContainerHeight > 100) {
    //     height = `${(descContainerHeight * 30) / 100}px`;
    //     this.descHiddenHeight = height;
    //     this.renderer.addClass(this.descContainer.nativeElement, 'hide');
    //     this.renderer.setStyle(this.descContainer.nativeElement, 'height', height);
    //     this.renderer.setStyle(this.descSeeMoreBtnContainer.nativeElement, 'display', 'block');
    //   } else {
    //     this.renderer.setStyle(this.descSeeMoreBtnContainer.nativeElement, 'display', 'none');
    //   }
    //   this.descHiddenHeight = height;
    // }
  }
  roundAvgRating() {
    return Math.round(this.product.average_rating);
  } // for rating star color

  checkAvailableVariantTier1(variantName: string) {
    return this.availableVariantsTier1.some(
      (vrnt: any) => vrnt === variantName
    );
  }
  checkAvailableVariantTier2(variantName: string) {
    return this.availableVariantsTier2.some(
      (vrnt: any) => vrnt === variantName
    );
  }

  onChangeQuantity(add: boolean = false) {
    if (add) {
      this.cartForm.patchValue({ quantity: this.cartForm.value.quantity + 1 });
      if (
        (this.selectedVariant &&
          this.selectedVariant.stock < this.cartForm.value.quantity) ||
        (!this.selectedVariant &&
          this.product.stock < this.cartForm.value.quantity)
      ) {
        return;
      } // check product stock
    } else {
      if (this.cartForm.value.quantity > 1) {
        this.cartForm.patchValue({
          quantity: this.cartForm.value.quantity - 1,
        });
      }
    }
  }
  onChangeQuantityTyping(event: any) {
    if (isPlatformBrowser(this.platformId)) {
      clearInterval(this.qtyInputChangeInterval);
      this.qtyInputChangeInterval = setInterval(() => {
        this.cartForm.patchValue({
          quantity: Number(
            event.value != null || event.value != '' ? event.value : 1
          ),
        });
        clearInterval(this.qtyInputChangeInterval);
      }, 500);
    }
  }

  // manage cart
  onAddToCart(
    buyNow: boolean = false,
    topBar: boolean = false,
    insideModal: boolean = false
  ) {
    if (this.product.has_variant) {
      if (this.product.id && this.selectedVariant) {
        this.addToCart(this.selectedVariant.id, buyNow, topBar, insideModal);
      } else {
        this.handleAddToCartError(
          'Silahkan pilih varian terlebih dahulu',
          topBar,
          insideModal
        );
      }
    } else {
      if (this.product.id) {
        this.addToCart(null, buyNow, topBar, insideModal);
      }
    }
  }

  addToCart(
    variantId: number | null,
    buyNow: boolean,
    topBar: boolean,
    insideModal: boolean
  ) {
    if (!this.addToCartLoading && !this.buyNowLoading) {
      buyNow ? (this.buyNowLoading = true) : (this.addToCartLoading = true);
      this.cartService
        .addCartItem(this.product.id, variantId!, this.cartForm.value.quantity)
        .subscribe(
          (res) => {
            // this.onCloseModal();
            this.addToCartError = null;
            if (buyNow) {
              // this.cartService.setBuyNowState(this.product.id);
              this.router.navigate(['/cart']);
            }
            this.addToCartLoading = false;
            this.buyNowLoading = false;
          },
          (error: any) => {
            if (error.code != 401 || error.code != '401') {
              // if not unauthorized error
              this.handleAddToCartError(error.message, topBar, insideModal);
            }
            this.addToCartLoading = false;
            this.buyNowLoading = false;
          }
        );
    }
  }

  private handleAddToCartError(
    message: any,
    topBar: boolean,
    insideModal: boolean
  ) {
    // this.addToCartError = message;
    // if(topBar) {
    //   if(!insideModal) {
    //     this.onOpenModal(this.addToCartModal);
    //   }
    // } else {
    //   this.onScrollToElem(this.detailContainer);
    // }
  }
  //

  // topbar
  // navigation
  onScrollToElem(sectionElem: any) {
    let headerOffset = 118;
    let offsetPosition = sectionElem.nativeElement.offsetTop - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
  // wishlist
  onAddToWishlist() {
    if (this.product.on_wishlist) {
      this.product.on_wishlist = false;
      this.wishlistService.removeFromWishlist(this.product.slug).subscribe(
        (data) => {},
        (error) => {
          this.product.on_wishlist = true;
        }
      );
    } else {
      this.product.on_wishlist = true;
      this.wishlistService.addToWishlist(this.product.slug).subscribe(
        (data) => {},
        (error) => {
          this.product.on_wishlist = false;
        }
      );
    }
  }
  @HostListener('window:scroll', [])
  onWindowScroll() {
    let detailBoxBounds =
      this.detailBoxContainer?.nativeElement.getBoundingClientRect();
    let reviewBounds =
      this.reviewContainer?.nativeElement.getBoundingClientRect();
    let discussionBounds =
      this.discussionContainer?.nativeElement.getBoundingClientRect();
    let recommendBounds =
      this.recommendationContainer?.nativeElement.getBoundingClientRect();
    if (
      document.body.scrollTop > 64 ||
      document.documentElement.scrollTop > 64
    ) {
      this.isShowTopBar = true;
    } else {
      this.isShowTopBar = false;
    }
    // navigation
    if (recommendBounds.top < 250) {
      this.activeNavigation = 'recommendation';
    } else if (discussionBounds.top <= 250) {
      this.activeNavigation = 'discussion';
    } else if (reviewBounds.top <= 250) {
      this.activeNavigation = 'review';
    } else if (detailBoxBounds.top <= 250) {
      this.activeNavigation = 'description';
    }
    // show sticky brochure close btn
    let brochureBounds =
      this.brochureContainer?.nativeElement.getBoundingClientRect();
    if (brochureBounds.top <= 170 && brochureBounds.bottom >= 850) {
      this.showStickyBrochureCloseBtn = true;
    } else {
      this.showStickyBrochureCloseBtn = false;
    }
  }

  onSelectVariantTier1(variant: any) {
    if (variant.array_images.length) {
      this.selectedImage = variant.array_images[0].large.path;
    } else {
      this.selectedImage = this.productDetail.images[0].large.path;
    }

    const variantTier1Name = variant.variant_name_1;
    this.selectedVariantTier1Name = variantTier1Name;
    if (this.selectedVariantTier1Name) {
      let availableVariantsTier2: any = [];
      const filterVariantHasSelectedTier1 = this.variants.filter(
        (tier1) => tier1.variant_name_1 === this.selectedVariantTier1Name
      ); // filter variant with selected variant tier 1 name
      filterVariantHasSelectedTier1.forEach((vrnt) => {
        availableVariantsTier2.push(vrnt.variant_name_2); // push variant name 2 to available variant tier 2 from filtered variant with selected variant tier 1 name
      });
      this.availableVariantsTier2 = availableVariantsTier2;
    } else {
      this.setupAvailableVariant();
    }
    this.checkSelectedVariantCombination();
  }

  onSelectVariantTier2(variant: any) {
    if (variant.array_images.length) {
      this.selectedImage = variant.array_images[0].large.path;
    } else {
      this.selectedImage = this.productDetail.images[0].large.path;
    }
    const variantTier2Name = variant.variant_name_2;
    this.selectedVariantTier1Name = variantTier2Name;
    if (this.selectedVariantTier2Name) {
      let availableVariantsTier1: any = [];
      const filterVariantHasSelectedTier2 = this.variants.filter(
        (tier2) => tier2.variant_name_2 === this.selectedVariantTier2Name
      ); // filter variant with selected variant tier 2 name
      filterVariantHasSelectedTier2.forEach((vrnt) => {
        availableVariantsTier1.push(vrnt.variant_name_1); // push variant name 1 to available variant tier 1 from filtered variant with selected variant tier 2 name
      });
      this.availableVariantsTier1 = availableVariantsTier1;
    } else {
      this.setupAvailableVariant();
    }
    this.checkSelectedVariantCombination();
  }
  checkSelectedVariantCombination() {
    const selectedVariantTier1Name = this.selectedVariantTier1Name
      ? this.selectedVariantTier1Name
      : null;
    const selectedVariantTier2Name = this.selectedVariantTier2Name
      ? this.selectedVariantTier2Name
      : null;
    const selectedVariant = this.variants.find(
      (vrt) =>
        vrt.variant_name_1 === selectedVariantTier1Name &&
        vrt.variant_name_2 === selectedVariantTier2Name
    );
    if (selectedVariant) {
      this.imageActiveIndex = 0;
      this.selectedVariant = selectedVariant;
      this.imagesDisplayed = selectedVariant.images;
    } else {
      this.imageActiveIndex = 0;
      this.selectedVariant = null;
      this.imagesDisplayed = this.productDetail.images;
    }
  }
  navigateToShop() {
    this.router.navigate(['/shop', this.shop?.slug]);
  }
  onShare() {
    let style = {};
    if (this.layoutService.isMobile$.value) {
      style = {
        'max-height': '100%',
        width: '100vh',
        height: '100vh',
        overflow: 'hidden',
      };
    } else {
      style = {
        'border-radius': '1rem',
        overflow: 'hidden',
      };
    }
    const ref = this.dialogService.open(FcSharingComponent, {
      dismissableMask: true,
      closable: true,
      showHeader: false,
      width: '500px',
      contentStyle: {
        padding: '0',
      },
      style: style,
    });
    ref.onClose.subscribe();
  }
}
